/**
 * Team API Service
 * Frontend service for communicating with backend team endpoints
 */

import { useAuth } from '../hooks/useAuth';
import { 
  Team, 
  TeamWithMembers, 
  TeamSummary, 
  TeamStrength, 
  CreateTeamFormData,
  TeamPosition,
  TeamCandidate,
  CandidateSource,
  CandidateStatus,
// @ts-ignore
  PositionPriority,
} from '../types/team';

const RAW_API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';
const API_BASE = RAW_API_BASE.replace(/\/$/, '').endsWith('/api')
  ? RAW_API_BASE.replace(/\/$/, '')
  : `${RAW_API_BASE.replace(/\/$/, '')}/api`;

// Helper to get auth token from localStorage
function getAuthToken(): string | null {
  const authData = localStorage.getItem('cd-people-auth');
  if (authData) {
    try {
      const { state } = JSON.parse(authData);
      return state.token || null;
    } catch {
      return null;
    }
  }
  return null;
}

// Helper to handle 401 responses - use Zustand logout (no reload to avoid flicker loop)
function handle401(response: Response, skipLogout = false) {
  if (response.status === 401 && !skipLogout) {
    useAuth.getState().logout();
  }
}

// Helper to build headers with auth
function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * List all teams (summary view)
 */
export async function listTeams(): Promise<TeamSummary[]> {
  const response = await fetch(`${API_BASE}/teams`, {
    method: 'GET',
    headers: getHeaders(),
  });

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to list teams: ${response.statusText}`);
  }

  const data = await response.json();
  return data.teams || [];
}

/**
 * Get team by ID (with full details and members)
 */
export async function getTeam(teamId: number): Promise<TeamWithMembers> {
  const response = await fetch(`${API_BASE}/teams/${teamId}`, {
    method: 'GET',
    headers: getHeaders(),
  });

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to get team: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Create a new team
 */
export async function createTeam(data: CreateTeamFormData): Promise<Team> {
  const response = await fetch(`${API_BASE}/teams`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  handle401(response);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to create team: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Update team
 */
export async function updateTeam(
  teamId: number,
  updates: Partial<CreateTeamFormData>
): Promise<Team> {
  const response = await fetch(`${API_BASE}/teams/${teamId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(updates),
  });

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to update team: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Delete/archive team
 */
export async function deleteTeam(teamId: number): Promise<void> {
  const response = await fetch(`${API_BASE}/teams/${teamId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to delete team: ${response.statusText}`);
  }
}

/**
 * Add member to team (triggers AI strength calculation)
 */
export async function addTeamMember(
  teamId: number,
  personId: number,
  status: 'onboarded' | 'potential'
): Promise<TeamWithMembers> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/members`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ person_id: personId, status }),
  });

  handle401(response);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to add member: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Remove member from team
 */
export async function removeTeamMember(
  teamId: number,
  personId: number
): Promise<TeamWithMembers> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/members/${personId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to remove member: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Update team member (status, notes)
 */
export async function updateTeamMember(
  teamId: number,
  personId: number,
  updates: { status?: 'onboarded' | 'potential'; notes?: string }
): Promise<any> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/members/${personId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(updates),
  });

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to update member: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Recalculate team strength using AI
 */
export async function recalculateTeamStrength(
  teamId: number
): Promise<{ team_strength: TeamStrength }> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/strength`, {
    method: 'POST',
    headers: getHeaders(),
  });

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to recalculate strength: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Helper: Convert 0-1.0 strength to percentage
 */
export function strengthToPercentage(strength: number): number {
  return Math.round(strength * 100);
}

/**
 * Helper: Get strength status label
 */
export function getStrengthStatus(
  strength: number
): 'excellent' | 'good' | 'fair' | 'poor' {
  if (strength >= 0.8) return 'excellent';
  if (strength >= 0.6) return 'good';
  if (strength >= 0.4) return 'fair';
  return 'poor';
}

/**
 * Helper: Get color for strength status
 */
export function getStrengthColor(status: 'excellent' | 'good' | 'fair' | 'poor'): string {
  switch (status) {
    case 'excellent':
      return '#10b981'; // green
    case 'good':
      return '#3b82f6'; // blue
    case 'fair':
      return '#f59e0b'; // amber
    case 'poor':
      return '#ef4444'; // red
    default:
      return '#6b7280'; // gray
  }
}

// ============================================================================
// POSITION API FUNCTIONS
// ============================================================================

export interface CreatePositionRequest {
  role: string;
  count_needed: number;
  skills_required?: string[];
  priority: 'critical' | 'important' | 'nice-to-have';
  status?: 'proposed' | 'confirmed';
  reasoning?: string;
  source?: 'ai' | 'user';
  // Extended JD fields
  description?: string;
  location_model?: string;
  location?: string;
  experience_level?: string;
  management_level?: string;
  education_level?: string;
  education_importance?: string;
  employment_type?: string;
  allocation_percentage?: number;
  responsibilities?: string[];
  skills_nice_to_have?: string[];
  rate_country?: string;
  ai_analysis?: string;
}

export interface UpdatePositionRequest {
  role?: string;
  count_needed?: number;
  skills_required?: string[];
  priority?: 'critical' | 'important' | 'nice-to-have';
  status?: 'proposed' | 'confirmed';
  reasoning?: string;
  // Extended JD fields
  description?: string;
  location_model?: string;
  location?: string;
  experience_level?: string;
  management_level?: string;
  education_level?: string;
  education_importance?: string;
  employment_type?: string;
  allocation_percentage?: number;
  responsibilities?: string[];
  skills_nice_to_have?: string[];
  rate_country?: string;
  ai_analysis?: string;
}

/**
 * Get all positions for a team
 */
export async function getTeamPositions(teamId: number): Promise<TeamPosition[]> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/positions`, {
    method: 'GET',
    headers: getHeaders(),
  });

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to get positions: ${response.statusText}`);
  }

  const data = await response.json();
  return data.positions || [];
}

/**
 * Create a new position for a team
 */
export async function createPosition(teamId: number, position: CreatePositionRequest): Promise<TeamPosition> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/positions`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(position),
  });

  handle401(response);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to create position: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Update a position
 */
export async function updatePosition(
  teamId: number, 
  positionId: number, 
  updates: UpdatePositionRequest
): Promise<TeamPosition> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/positions/${positionId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(updates),
  });

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to update position: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Delete a position
 */
export async function deletePosition(teamId: number, positionId: number): Promise<void> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/positions/${positionId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to delete position: ${response.statusText}`);
  }
}

/**
 * Confirm a proposed position
 */
export async function confirmPosition(teamId: number, positionId: number): Promise<TeamPosition> {
  return updatePosition(teamId, positionId, { status: 'confirmed' });
}

// ============================================================================
// CANDIDATE API FUNCTIONS
// ============================================================================

export interface AddCandidateRequest {
  person_id: number;
  source: CandidateSource;
  status?: CandidateStatus;
  match_score?: number;
  match_reasoning?: string;
}

export interface UpdateCandidateRequest {
  status?: CandidateStatus;
  match_score?: number;
  match_reasoning?: string;
}

/**
 * Get all candidates for a team (across all positions)
 */
export async function getTeamCandidates(teamId: number): Promise<TeamCandidate[]> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/candidates`, {
    method: 'GET',
    headers: getHeaders(),
  });

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to get candidates: ${response.statusText}`);
  }

  const data = await response.json();
  return data.candidates || [];
}

/**
 * Add a candidate to a position
 */
export async function addCandidate(
  teamId: number, 
  positionId: number, 
  candidate: AddCandidateRequest
): Promise<TeamCandidate> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/positions/${positionId}/candidates`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(candidate),
  });

  handle401(response);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to add candidate: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Update a candidate
 */
export async function updateCandidate(
  teamId: number,
  positionId: number,
  personId: number,
  updates: UpdateCandidateRequest
): Promise<TeamCandidate> {
  const response = await fetch(
    `${API_BASE}/teams/${teamId}/positions/${positionId}/candidates/${personId}`,
    {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(updates),
    }
  );

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to update candidate: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Remove a candidate from a position
 */
export async function removeCandidate(
  teamId: number,
  positionId: number,
  personId: number
): Promise<void> {
  const response = await fetch(
    `${API_BASE}/teams/${teamId}/positions/${positionId}/candidates/${personId}`,
    {
      method: 'DELETE',
      headers: getHeaders(),
    }
  );

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to remove candidate: ${response.statusText}`);
  }
}

/**
 * Select a candidate (confirm them for a position)
 */
export async function selectCandidate(
  teamId: number,
  positionId: number,
  personId: number
): Promise<TeamCandidate> {
  return updateCandidate(teamId, positionId, personId, { status: 'selected' });
}

/**
 * Add a person directly to a team (shortcut - adds to unassigned pool or creates position)
 */
export async function addPersonToTeam(
  teamId: number,
  personId: number,
  options?: { positionId?: number; source?: CandidateSource }
): Promise<TeamCandidate> {
  const positionId = options?.positionId || 0; // 0 = unassigned pool
  const source = options?.source || 'user_selected';

  return addCandidate(teamId, positionId, {
    person_id: personId,
    source,
    status: 'selected',
  });
}

// ============================================================================
// POSITION AI API FUNCTIONS
// ============================================================================

/**
 * Generate AI analysis for a position within team/project context
 */
export async function generatePositionAIAnalysis(
  teamId: number,
  positionId: number
): Promise<{ ai_analysis: string }> {
  const response = await fetch(
    `${API_BASE}/teams/${teamId}/positions/${positionId}/ai-analysis`,
    {
      method: 'POST',
      headers: getHeaders(),
    }
  );

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to generate AI analysis: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * AI auto-fill position fields based on role and/or description and team context.
 * When only description is provided, the AI will also suggest a role title.
 */
export async function aiAutoFillPosition(
  teamId: number,
  data: { role?: string; description?: string }
): Promise<Partial<TeamPosition>> {
  const response = await fetch(
    `${API_BASE}/teams/${teamId}/positions/ai-fill`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    }
  );

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to auto-fill position: ${response.statusText}`);
  }

  return await response.json();
}

// ============================================================================
// JD EXPORT / IMPORT API FUNCTIONS
// ============================================================================

export type JdFormat = 'md' | 'pdf' | 'docx';

export interface JdExportOptions {
  template?: 'standard' | 'compact' | 'detailed';
  sections?: string[];
  company_name?: string;
  format?: JdFormat;
  include_infographic?: boolean;
}

export interface JdExportResult {
  markdown: string;
  suggested_filename: string;
}

export interface JdBlobResult {
  blob: Blob;
  suggested_filename: string;
}

export interface JdParsePayload {
  text: string;
  source?: 'upload' | 'paste' | 'drive';
  file_meta?: { name?: string; mime?: string; size?: number };
}

export interface JdParseResult {
  extracted: Partial<TeamPosition>;
  confidence: Record<string, number>;
  warnings: string[];
}

/**
 * Generate a JD preview in MD (returns JSON) or PDF/DOCX (returns blob).
 */
export async function exportJdPreview(
  teamId: number,
  positionId: number,
  options?: JdExportOptions
): Promise<JdExportResult> {
  const response = await fetch(
    `${API_BASE}/teams/${teamId}/positions/${positionId}/export-jd-preview`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(options || {}),
    }
  );

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to export JD: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Export JD as a downloadable file (PDF or DOCX). Returns a Blob.
 */
export async function exportJdBlob(
  teamId: number,
  positionId: number,
  options: JdExportOptions
): Promise<JdBlobResult> {
  const format = options.format || 'pdf';
  const response = await fetch(
    `${API_BASE}/teams/${teamId}/positions/${positionId}/export-jd-preview`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(options),
    }
  );

  handle401(response);
  if (!response.ok) {
    throw new Error(`Failed to export JD as ${format}: ${response.statusText}`);
  }

  const disposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
  const suggested_filename = filenameMatch?.[1] || `job_description.${format}`;
  const blob = await response.blob();
  return { blob, suggested_filename };
}

export interface InfographicResult {
  image_base64: string;
  mime_type: string;
}

/**
 * Get stored infographic for a position (returns null fields if not yet generated).
 */
export async function getPositionInfographic(
  teamId: number,
  positionId: number
): Promise<InfographicResult | null> {
  const response = await fetch(
    `${API_BASE}/teams/${teamId}/positions/${positionId}/infographic`,
    { headers: getHeaders() }
  );
  handle401(response);
  if (!response.ok) return null;
  const data = await response.json();
  if (!data.image_base64 || data.image_base64.length < 256) return null;
  return { image_base64: data.image_base64, mime_type: data.mime_type };
}

/**
 * Generate (or regenerate) a position/role infographic and persist it.
 */
export async function generatePositionInfographic(
  teamId: number,
  positionId: number
): Promise<InfographicResult> {
  const response = await fetch(
    `${API_BASE}/teams/${teamId}/positions/${positionId}/generate-infographic`,
    {
      method: 'POST',
      headers: getHeaders(),
    }
  );

  handle401(response);
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || `Failed to generate infographic: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Parse raw JD text and extract structured position fields
 */
export async function parseImportedJd(
  teamId: number,
  payload: JdParsePayload
): Promise<JdParseResult> {
  const response = await fetch(
    `${API_BASE}/teams/${teamId}/positions/import-jd-parse`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    }
  );

  handle401(response, true);
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || `Failed to parse JD: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Upload a file (PDF, DOCX, TXT, etc.) for server-side text extraction + JD parsing
 */
export async function uploadJdFile(
  teamId: number,
  file: File,
): Promise<JdParseResult> {
  const formData = new FormData();
  formData.append('file', file);

  const token = getAuthToken();
  const response = await fetch(
    `${API_BASE}/teams/${teamId}/positions/import-jd-upload`,
    {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    }
  );

  handle401(response, true);
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || `Failed to process file: ${response.statusText}`);
  }

  return await response.json();
}
